import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import {
    Alert,
    Button,
    Form,
    FormField,
    Input,
    Link,
    Multiselect,
    SpaceBetween,
} from "@cloudscape-design/components";
import {
    setPageTitle,
    appendInfoMessage,
    selectOnboardingRequest,
    removeInfoMessage,
    resetOnboardingRequest,
} from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import { submitOnboardingRequest, getUserPipelines } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { useSelector } from "react-redux";
import { SentinelLoadingStatus } from "src/core/sentinel/types";
import { useInfoSection } from "src/web/components/SentinelLayout/index";
import { DEFAULT_CR_LIMIT, SLACK_CHANNEL, SLACK_URL, TEAMS_RECORD_URL } from "src/web/constants";

const extractErrorMessage = (error: unknown): string => {
    if (!error) return "";
    if (typeof error === "string") return error;
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
};

export interface SentinelOnboardingPageProps extends SentinelPageProps {
    readOnly?: boolean;
    sentinelController: SentinelController;
}

export const OnboardingPage: React.FC<SentinelOnboardingPageProps> = (
    props: SentinelOnboardingPageProps
) => {
    const [, setToolsOpen] = useInfoSection();
    const [alias, setAlias] = useState("");
    const [teamName, setTeamName] = useState("");
    const [teamEmail, setTeamEmail] = useState("");
    const [crLimit, setCrLimit] = useState("");
    const [selectedPipelines, setSelectedPipelines] = useState<any[]>([]);
    const [availablePipelines, setAvailablePipelines] = useState<any[]>([]);
    const [pipelinesLoading, setPipelinesLoading] = useState(true);
    const [pipelinesError, setPipelinesError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const onboardingRequest = useSelector(selectOnboardingRequest);
    const status = onboardingRequest?.status;
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageTitle("Onboarding Page"));
    }, []);

    useEffect(() => {
        setAlias(props.sentinelController.userIdentity.username);
    }, [props.sentinelController.userIdentity.username]);

    useEffect(() => {
        const fetchPipelines = async () => {
            if (!props.sentinelController.userIdentity.username) {
                return;
            }
            setPipelinesLoading(true);
            setPipelinesError("");
            try {
                const response = await dispatch(
                    getUserPipelines({
                        sentinelController: props.sentinelController,
                        userId: props.sentinelController.userIdentity.username,
                    })
                ).unwrap();
                const pipelineNames = (response as any)?.payload?.pipelineNames ?? [];
                setAvailablePipelines(
                    pipelineNames.map((name: string) => ({
                        label: name,
                        value: name,
                    }))
                );
            } catch (error) {
                setPipelinesError("Failed to load pipelines. Please try again.");
            } finally {
                setPipelinesLoading(false);
            }
        };
        fetchPipelines();
    }, [props.sentinelController.userIdentity.username]);

    useEffect(() => {
        return () => {
            setToolsOpen(false);
        };
    }, []);

    useEffect(() => {
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    useEffect(() => {
        if (status === SentinelLoadingStatus.FINISHED) {
            dispatch(removeInfoMessage({ id: "message_onboardingrequest_submit" }));
        }
    }, [status]);

    const pipelineNames = selectedPipelines.map((p) => p.value);
    const isEmailValid = teamEmail === "" || /^[a-zA-Z0-9._%+-]+@amazon.com$/.test(teamEmail);
    const isCrLimitValid = crLimit === "" || (/^\d+$/.test(crLimit) && Number(crLimit) >= 0);
    const crLimitPausesReviews = isCrLimitValid && crLimit !== "" && Number(crLimit) === 0;
    const isFormValid =
        teamName !== "" && pipelineNames.length > 0 && isEmailValid && isCrLimitValid;

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError("");
        setSubmitted(false);
        const payload = {
            requesterId: alias,
            pipelineNames: pipelineNames,
            teamName: teamName,
            teamEmail: teamEmail,
            ...(crLimit === "" ? {} : { crLimit: Number(crLimit) }),
        };
        dispatch(appendInfoMessage({
            type: "info",
            loading: true,
            content: "Request submission in progress",
            dismissLabel: "Dismiss",
            id: "message_onboardingrequest_submit",
        }));
        try {
            await dispatch(
                submitOnboardingRequest({
                    sentinelController: props.sentinelController,
                    onboardingRequestInput: payload,
                })
            ).unwrap();
            setSubmitting(false);
            setSubmitted(true);
            setTeamName("");
            setTeamEmail("");
            setCrLimit("");
            setSelectedPipelines([]);
        } catch (error) {
            setSubmitting(false);
            setSubmitError(extractErrorMessage(error) || "The service returned no message.");
            dispatch(removeInfoMessage({ id: "message_onboardingrequest_submit" }));
            dispatch(resetOnboardingRequest());
        }
    };

    const teamChecklist = (
        <SpaceBetween size="xs">
            <div>
                If that is unexpected, check both of these before resubmitting:
            </div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                    <strong>Team Name</strong> matches your team exactly as it is recorded in{" "}
                    <Link href={TEAMS_RECORD_URL} external={true}>
                        Teams
                    </Link>
                    , including capitalisation and spacing.
                </li>
                <li>
                    <strong>You are a member of that team</strong> in the same record.
                </li>
            </ul>
        </SpaceBetween>
    );

    return (
        <SentinelBasePage>
            <SpaceBetween size="l">
                {submitError && (
                    <Alert
                        type="error"
                        header="Onboarding request failed"
                        dismissible
                        onDismiss={() => setSubmitError("")}
                    >
                        <SpaceBetween size="s">
                            <div>{submitError}</div>
                            {teamChecklist}
                            <div>
                                If both are correct, retry, or contact us on <a href={SLACK_URL}>{SLACK_CHANNEL}</a> Slack channel   
                            </div>
                        </SpaceBetween>
                    </Alert>
                )}

                {submitted && (
                    <Alert
                        type="success"
                        header="Request submitted"
                        dismissible
                        onDismiss={() => setSubmitted(false)}
                    >
                        Your pipelines are now recorded. Nothing further is needed from you. Packages
                        appear after the next Monday 00:00 UTC scan, and code reviews follow once
                        dependencies have been collected. Check Onboarded Teams to confirm what was onboarded.
                    </Alert>
                )}

                <Form
                    actions={
                        <SpaceBetween direction="vertical" size="s">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="primary"
                                    disabled={!isFormValid || submitting}
                                    onClick={() => handleSubmit()}
                                >
                                Submit Request
                                </Button>
                            </SpaceBetween>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween size="l">
                        <FormField>
                            <div className="formfield-margin">
                                <strong>Requester:</strong> {alias}
                            </div>
                        </FormField>

                        <FormField
                            label="Team Name *"
                            description={
                                <>
                                    Must match your team exactly as recorded in{" "}
                                    <Link href={TEAMS_RECORD_URL} external={true}>
                                        Teams
                                    </Link>
                                    ,and you must be a member of it.
                                </>
                            }
                        >
                            <Input
                                type="text"
                                placeholder={`Enter Team Name as per Teams record (${TEAMS_RECORD_URL})`}
                                value={teamName}
                                onChange={(event) => setTeamName(event.detail.value)}
                            />
                        </FormField>

                        <FormField label="Team Email" errorText={teamEmail && !isEmailValid ? "Enter a valid @amazon.com email" : ""}>
                            <Input
                                type="text"
                                placeholder="Enter team email"
                                value={teamEmail}
                                onChange={(event) => setTeamEmail(event.detail.value)}
                            />
                        </FormField>

                        <FormField
                            label="Pipeline Name *"
                            description="Select pipelines you have access to"
                        >
                            {pipelinesError && (
                                <Alert type="error">{pipelinesError}</Alert>
                            )}
                            <Multiselect
                                selectedOptions={selectedPipelines}
                                onChange={({ detail }) => setSelectedPipelines([...detail.selectedOptions])}
                                options={availablePipelines}
                                filteringType="auto"
                                placeholder="Search and select pipelines"
                                loadingText="Loading pipelines..."
                                statusType={pipelinesLoading ? "loading" : "finished"}

                                tokenLimit={5}
                            />
                        </FormField>

                        <FormField
                            label="Code Review Limit"
                            description={
                                <>
                                    The most code reviews Dependency Sentinel keeps open for your team
                                    at once. It raises more as you merge or close them. Default: 5
                                </>
                            }
                            errorText={
                                crLimit && !isCrLimitValid ? "Enter a whole number of 0 or more" : ""
                            }
                            constraintText={
                                crLimitPausesReviews
                                    ? "A limit of 0 stops new code reviews being raised for your team."
                                    : ""
                            }
                        >
                            <Input
                                type="text"
                                inputMode="numeric"
                                placeholder={String(DEFAULT_CR_LIMIT)}
                                value={crLimit}
                                onChange={(event) => setCrLimit(event.detail.value)}
                            />
                        </FormField>
                        <Alert type="info" header="What to expect after onboarding">
                            <SpaceBetween size="xs">
                                <div>
                                    Onboarding records your pipelines; it does not scan them immediately.
                                    <strong> You may not see packages, dependencies, or code reviews straight
                                    away.</strong> Each step runs on its own schedule:
                                </div>
                                <ol style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>
                                        <strong>Packages</strong> — scanned weekly.
                                    </li>
                                    <li>
                                        <strong>Dependencies and risk</strong> — scanned twice weekly.
                                    </li>
                                    <li>
                                        <strong>Code reviews</strong> — raised daily.
                                    </li>
                                </ol>
                                <div>
                                    Allow up to a week before the first code reviews arrive. Open Info for the full schedule.
                                </div>
                                <div>
                                    By default, at most {DEFAULT_CR_LIMIT} reviews are kept open for your team
                                    at a time, and more are raised as you merge or close them. Set a Code Review
                                    Limit to change that. 
                                </div>
                            </SpaceBetween>
                        </Alert>
                    </SpaceBetween>
                </Form>
            </SpaceBetween>
        </SentinelBasePage>
    );
};
